import { Directive, ElementRef, Input, ComponentFactoryResolver, ViewContainerRef, TemplateRef } from "@angular/core";
import { EventEmitter, Output, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { MentionListComponent } from './mention-list.component';
import { insertValue, getCaretPosition, setCaretPosition } from './mention-utils';
import { ElasticSearchService } from "../../_services/elastic-search.service";

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_ESCAPE = 27;
const KEY_SPACE = 32;
const KEY_LEFT = 37;
const KEY_UP = 38;
const KEY_RIGHT = 39;
const KEY_DOWN = 40;
const KEY_2 = 50;

/**
 * Angular 2 Mentions.
 * https://github.com/dmacfarlane/angular2-mentions
 *
 * Copyright (c) 2017 Dan MacFarlane
 */
@Directive({
  selector: '[mention]',
  host: {
    '(keydown)': 'keyHandler($event)',
    '(blur)': 'blurHandler($event)'
  }
})
export class MentionDirective implements OnInit, OnChanges {

  @Input() set mention(items: any[]) {
    this.items = items;
  }
  @Output() selectedSuggestion = new EventEmitter();
  @Input() set mentionConfig(config: any) {
    this.triggerChars = config.triggerChars || this.triggerChars;
    this.labelKey = config.labelKey || this.labelKey;
    this.disableSearch = config.disableSearch || this.disableSearch;
    this.maxItems = config.maxItems || this.maxItems;
    this.mentionSelect = config.mentionSelect || this.mentionSelect;
  }

  // template to use for rendering list items
  @Input() mentionListTemplate: TemplateRef<any>;

  // event emitted whenever the search term changes
  @Output() searchTerm = new EventEmitter();

  // the array of characters that will trigger the menu behavior
  private triggerChars: string[] = ["@", "#"];

  // the character that will trigger the menu behavior
  private triggeredWith: string;

  // option to specify the field in the objects to be used as the item label
  private labelKey: string = 'label';

  // option to diable internal filtering. can be used to show the full list returned
  // from an async operation (or allows a custom filter function to be used - in future)
  private disableSearch: boolean = false;

  // option to limit the number of items shown in the pop-up menu
  private maxItems: number = -1;

  // optional function to format the selected item before inserting the text
  private mentionSelect: (item: any) => (string) = (item: any) => this.triggeredWith + item[this.labelKey]

  searchString: string;
  startPos: number;
  items: any[];
  startNode;
  searchList: MentionListComponent;
  stopSearch: boolean;
  iframe: any; // optional

  constructor(
    private _element: ElementRef,
    private _componentResolver: ComponentFactoryResolver,
    private _viewContainerRef: ViewContainerRef,
    private _elasticSearchService: ElasticSearchService,

  ) { }

  ngOnInit() {
    // if (this.items && this.items.length>0) {
    //   if (typeof this.items[0] == 'string') {
    //     // convert strings to objects
    //     const me = this;
    //     this.items = this.items.map(function(label){
    //       let object = {};
    //       object[me.labelKey] = label;
    //       return object;
    //     });
    //   }
    //   // remove items without an labelKey (as it's required to filter the list)
    //   this.items = this.items.filter(e => e[this.labelKey]);
    //   this.items.sort((a,b)=>a[this.labelKey].localeCompare(b[this.labelKey]));
    //   if (this.searchList && !this.searchList.hidden) {
    //     this.updateSearchList();
    //   }
    // }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mention']) {
      this.ngOnInit();
    }
  }

  setIframe(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
  }

  stopEvent(event: any) {
    //if (event instanceof KeyboardEvent) { // does not work for iframe
    if (!event.wasClick) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  blurHandler(event: any) {
    this.stopEvent(event);
    this.stopSearch = true;
    if (this.searchList) {
      this.searchList.hidden = true;
    }
  }

  keyHandler(event: any, nativeElement: HTMLInputElement = this._element.nativeElement) {
    let pos = getCaretPosition(nativeElement, this.iframe);
    let charPressed = event.key;
    if (!charPressed) {
      let charCode = event.which || event.keyCode;
      if (!event.shiftKey && (charCode >= 65 && charCode <= 90)) {
        charPressed = String.fromCharCode(charCode + 32);
      }
      else if (event.shiftKey && charCode === KEY_2) {
        charPressed = this.triggerChars;
      }
      else {
        // TODO (dmacfarlane) fix this for non-alpha keys
        // http://stackoverflow.com/questions/2220196/how-to-decode-character-pressed-from-jquerys-keydowns-event-handler?lq=1
        charPressed = String.fromCharCode(event.which || event.keyCode);
      }
    }
    if (event.keyCode == KEY_ENTER && event.wasClick && pos < this.startPos) {
      // put caret back in position prior to contenteditable menu click
      pos = this.startNode.length;
      setCaretPosition(this.startNode, pos, this.iframe);
    }
    /*
    let word = this.getCurrentWordOnCaretPosition(pos);
    if(word){
      this.triggeredWith = word.subscribe(0,1);
      this.startPos = pos;
      this.startNode = (this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()).anchorNode;
      this.stopSearch = false;
      this.searchString = word.substring(1);
      this.showSearchList(nativeElement);
      this.updateSearchList();
    }
    */

    let triggeredWith = this.triggerChars.filter(item => item == charPressed)[0];
    if (triggeredWith) {
      this.triggeredWith = triggeredWith;
      this.startPos = pos;
      this.startNode = (this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()).anchorNode;
      this.stopSearch = false;
      this.searchString = null;
    }
    else {
      if (event.keyCode !== KEY_SHIFT &&
        !event.metaKey &&
        !event.altKey &&
        !event.ctrlKey
      ) {
        if (event.keyCode === KEY_BACKSPACE && pos > 0) {

          this.stopSearch = this.searchString && this.searchString.length != 0 ? this.stopSearch : true;
          if (this.searchList)
            this.searchList.hidden = this.stopSearch ? this.stopSearch : false;
          pos--;
        }
        else if (this.searchList && !this.searchList.hidden) {
          if (event.keyCode === KEY_TAB || event.keyCode === KEY_ENTER) {
            this.stopEvent(event);
            this.searchList.hidden = true;
            // value is inserted without a trailing space for consistency
            // between element types (div and iframe do not preserve the space)
            insertValue(nativeElement, this.startPos, pos,
              this.mentionSelect(this.searchList.activeItem), this.iframe);
            // fire input event so angular bindings are updated
            if ("createEvent" in document) {
              var evt = document.createEvent("HTMLEvents");
              evt.initEvent("input", false, true);
              nativeElement.dispatchEvent(evt);
            }
            this.selectedSuggestion.emit(this.searchList.activeItem);
            // this.startPos = -1;
            return false;
          }
          else if (event.keyCode === KEY_ESCAPE) {
            this.stopEvent(event);
            this.searchList.hidden = true;
            this.stopSearch = true;
            return false;
          }
          else if (event.keyCode === KEY_DOWN) {
            this.stopEvent(event);
            this.searchList.activateNextItem();
            return false;
          }
          else if (event.keyCode === KEY_SPACE) {
            return false;
          }
          else if (event.keyCode === KEY_UP) {
            this.stopEvent(event);
            this.searchList.activatePreviousItem();
            return false;
          }
        }
        let mention: string = this.getCurrentWordOnCaretPosition(pos); //val.substring(this.startPos + 1, pos);

        if (mention && !(event.keyCode === KEY_LEFT || event.keyCode === KEY_RIGHT)) {
          if (this.startPos != pos - mention.length) {
            this.startPos = pos - mention.length;
          }
          if (event.keyCode !== KEY_BACKSPACE) {
            mention += charPressed;
          }
          this.triggeredWith = mention.substring(0, 1);
          this.searchString = mention.substring(1);
          if (this.searchString.length > 2) {
            this.updateSearchList();
          }

        } else {
          this.stopSearch = true;
        }
      }
    }
  }
  ReturnWord(text, caretPos) {
    var preText = text.substring(0, caretPos).replace(/(?:\r\n|\r|\n)/g, ' ');
    if (preText.lastIndexOf(" ") > 0) {
      var words = preText.split(" ");
      return words[words.length - 1]; //return last word
    }
    else {
      return preText;
    }
  }
  getMaximumIndexOfTriggeredChars(text: string) {
    let maxIndex = 0;
    this.triggerChars.filter(char => {
      if (text.lastIndexOf(char) > maxIndex) maxIndex = text.lastIndexOf(char)
    });
    return maxIndex;
  }
  isWordContainsTriggerChars(word: string) {
    let isContain = this.triggerChars.filter(char => {
      return word.indexOf(char) != -1;
    }).length > 0;
    return isContain;
  }
  getCurrentWordOnCaretPosition(caretPos) {
    var word = this.ReturnWord(this._element.nativeElement.value, caretPos);
    if (word && this.isWordContainsTriggerChars(word)) {
      let triggeredWithIndex = this.getMaximumIndexOfTriggeredChars(word);
      return word.substring(triggeredWithIndex);
    }
  }
  updateSearchList() {
    let searchString = this.searchString ? this.searchString.toLowerCase() : this.searchString;

    if (searchString && searchString.length > 2) {
      let types = [];
      if (this.triggeredWith == "@") {
        types = ["product", "brand", "user", "category"]
      } else if (this.triggeredWith == "#") {
        types = ["hashtag"]
      };
      this._elasticSearchService.searchBy("SearchTermAndType", { searchTerm: searchString.toLowerCase(), size: 35, types: types }).subscribe((hits: any[]) => {
        try {
          if (hits.length != 0) {
            this.showSearchList(this._element.nativeElement);
            this.searchList.hidden = true;
            if (this.searchList) {
              this.searchList.hidden = false;
              this.searchList.items = hits;
            }
          } else if (hits.length < 1) {
            if (this.triggeredWith == "@") {
              hits.push({ name: "Markanı tanımlayalım!", type: "advice" });
              this.showSearchList(this._element.nativeElement);
              this.searchList.items = hits;
            } else
              this.searchList.hidden = true;
          } else {
            this.searchList.hidden = true;
          }
        } catch (e) {

        }
      });
    } else {
      this.searchList.hidden = true;
    }


  }

  showSearchList(nativeElement: HTMLInputElement) {
    if (!this.searchList) {
      let componentFactory = this._componentResolver.resolveComponentFactory(MentionListComponent);
      let componentRef = this._viewContainerRef.createComponent(componentFactory);
      this.searchList = componentRef.instance;
      this.searchList.position(nativeElement, this.iframe);
      this.searchList.itemTemplate = this.mentionListTemplate;
      this.searchList.labelKey = this.labelKey;
      componentRef.instance['itemClick'].subscribe(() => {
        nativeElement.focus();
        let fakeKeydown = { "keyCode": KEY_ENTER, "wasClick": true };
        this.keyHandler(fakeKeydown, nativeElement);
      });
    }
    else {
      this.searchList.activeIndex = 0;
      this.searchList.position(nativeElement, this.iframe);
      window.setTimeout(() => this.searchList.resetScroll());
    }
  }
}



// WEBPACK FOOTER //
// C:/Users/Lenovo/Desktop/bigadvicefirestore/src/app/directive/mention/mention.directive.ts